import { useState, useEffect, useCallback } from "react";
import { DateField } from "@/components/ui/date-field";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtEur, fmtDate } from "@/lib/format";
import { BarChart3, FileDown } from "lucide-react";
import { Combobox } from "@/components/ui/combobox";

const monthDefault = () => {
  const n = new Date();
  const start = new Date(n.getFullYear(), n.getMonth(), 1);
  const end = new Date(n.getFullYear(), n.getMonth() + 1, 0);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end) };
};

const statusBadge = (s) => ({ paid: "bg-emerald-100 text-emerald-700", late: "bg-red-100 text-red-700", open: "bg-amber-100 text-amber-700" }[s] || "bg-slate-100 text-slate-600");
const statusLabel = (s) => ({ paid: "Betaald", late: "Te laat", open: "Open" }[s] || s);

export default function Reports() {
  const [clients, setClients] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [clientId, setClientId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [range, setRange] = useState(monthDefault());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
  }, []);

  const run = useCallback(() => {
    setLoading(true);
    const params = { start: range.start, end: range.end };
    if (tenantId) params.tenant_id = tenantId;
    else if (clientId) params.client_id = clientId;
    api.get("/reports/summary", { params }).then((r) => setReport(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [range, clientId, tenantId]);

  useEffect(() => { run(); }, [run]);

  const tenantOptions = clientId ? tenants.filter((t) => t.client_id === clientId) : tenants;
  const t = report?.totals || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Rapportages</h1>
        <p className="text-slate-500">Overzicht per opdrachtgever of debiteur binnen een periode. Standaard: huidige maand.</p>
      </div>

      <Card className="p-4 flex flex-wrap items-end gap-3" data-testid="report-filters">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Opdrachtgever</label>
          <div className="min-w-[180px]"><Combobox testId="report-client-select" value={clientId} onChange={(v) => { setClientId(v); setTenantId(""); }} placeholder="Alle opdrachtgevers" searchPlaceholder="Zoek…" options={[{ value: "", label: "Alle opdrachtgevers" }, ...clients.map((c) => ({ value: c.id, label: c.name }))]} /></div>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Debiteur</label>
          <div className="min-w-[180px]"><Combobox testId="report-tenant-select" value={tenantId} onChange={setTenantId} placeholder="Alle debiteuren" searchPlaceholder="Zoek…" options={[{ value: "", label: "Alle debiteuren" }, ...tenantOptions.map((t) => ({ value: t.id, label: `${t.first_name} ${t.last_name}` }))]} /></div>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Van</label>
          <DateField testId="report-from" value={range.start} onChange={(v) => setRange({ ...range, start: v })} className="!w-40 h-9" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">t/m</label>
          <DateField testId="report-to" value={range.end} onChange={(v) => setRange({ ...range, end: v })} className="!w-40 h-9" />
        </div>
        <Button className="bg-blue-900 hover:bg-blue-800" onClick={run} data-testid="report-run"><BarChart3 className="h-4 w-4 mr-2" /> Genereer</Button>
        <Button variant="outline" onClick={() => { setRange(monthDefault()); setClientId(""); setTenantId(""); }} data-testid="report-reset">Deze maand</Button>
      </Card>

      {loading && <p className="text-sm text-slate-400">Laden...</p>}

      {report && !loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="report-totals">
            {[["Gefactureerd", t.billed, "text-slate-800"], ["Betaald", t.paid, "text-emerald-700"], ["Openstaand", t.open, "text-amber-700"], ["Te laat", t.overdue, "text-red-600"], ["Uitbetaald", t.payout, "text-blue-800"]].map(([l, v, cls]) => (
              <Card key={l} className="p-4"><div className="text-xs text-slate-400">{l}</div><div className={`text-xl font-bold ${cls}`}>{fmtEur(v || 0)}</div></Card>
            ))}
          </div>

          <Card className="p-5">
            <h3 className="font-heading font-semibold mb-3">Termijnen ({report.payments.length})</h3>
            {report.payments.length === 0 ? <p className="text-sm text-slate-400">Geen termijnen in deze periode.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Debiteur</TableHead><TableHead>Maand</TableHead><TableHead>Vervaldatum</TableHead><TableHead>Bedrag</TableHead><TableHead>Boete</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {report.payments.map((p, i) => (
                    <TableRow key={i} data-testid={`report-payment-${i}`}>
                      <TableCell className="font-medium">{p.tenant_name}</TableCell>
                      <TableCell>{p.period}</TableCell>
                      <TableCell>{fmtDate(p.due_date)}</TableCell>
                      <TableCell>{fmtEur(p.amount)}</TableCell>
                      <TableCell>{p.penalty ? fmtEur(p.penalty) : "—"}</TableCell>
                      <TableCell><Badge className={statusBadge(p.status)}>{statusLabel(p.status)}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>

          {report.payouts.length > 0 && (
            <Card className="p-5">
              <h3 className="font-heading font-semibold mb-3">Uitbetalingen ({report.payouts.length})</h3>
              <Table>
                <TableHeader><TableRow><TableHead>Begunstigde</TableHead><TableHead>Factuur</TableHead><TableHead>Datum</TableHead><TableHead>Bedrag</TableHead><TableHead>Fee</TableHead><TableHead>Uitbetaald</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {report.payouts.map((p, i) => (
                    <TableRow key={i} data-testid={`report-payout-${i}`}>
                      <TableCell className="font-medium">{p.debtor_name}</TableCell>
                      <TableCell>{p.invoice_number || "—"}</TableCell>
                      <TableCell>{fmtDate(p.date)}</TableCell>
                      <TableCell>{fmtEur(p.amount)}</TableCell>
                      <TableCell className="text-red-600">-{fmtEur(p.fee)}</TableCell>
                      <TableCell className="text-emerald-700">{fmtEur(p.payout)}</TableCell>
                      <TableCell><Badge className={p.payout_done ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{p.payout_done ? "Uitbetaald" : "Openstaand"}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
